import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { aplicarEscopoTriagem } from '@/components/kanban/triagem/triagemPedidoUtils';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';

/**
 * Quantos pedidos MEUS estão parados na biometria — para o chip "Bio" do topo.
 *
 * Douglas 06/08: "iremos ligar muito para os clientes de biometria... coloca uma
 * nova Bio e quantos pedidos tem lá, e quando eu apertar abre este modal".
 *
 * Usa o MESMO escopo da Triagem Biometria (`aplicarEscopoTriagem`): consultor vê
 * os próprios, BKO vê as equipes mapeadas. Se o chip contasse diferente da tela
 * que ele abre, o número mentiria.
 */
export function useBiometriaKpiTopo() {
  const { profile } = useAuth();
  const { refetchInterval } = useSmartPolling({ weight: 'B', baseInterval: POLLING_INTERVALS.PESO_B });

  const { data } = useQuery({
    queryKey: ['biometria-kpi-topo', profile?.id],
    enabled: !!profile?.id,
    refetchInterval,
    staleTime: 30_000,
    queryFn: async () => {
      let q = supabase
        .from('v_planilha_mae')
        .select('pedido_id, entrada_coluna_em, gestor_telefone, gestor_whatsapp')
        .eq('coluna_atual', 'BIOMETRIA')
        .eq('quadro_atual', 'CONTRATO_PRIME');
      q = await aplicarEscopoTriagem(q, profile);
      const { data: itens, error } = await q;
      if (error) throw error;

      // O chip conta PEDIDO, não item — é o que a triagem mostra ("55 pedidos").
      // Guarda, por pedido, o telefone do gestor e desde quando ele está parado:
      // é com isso que se mede quem já foi trabalhado.
      const porPedido = new Map<string, { desde: string; suf: string }>();
      let maisAntigo: string | null = null;
      type Linha = { pedido_id: string; entrada_coluna_em: string | null; gestor_telefone: string | null; gestor_whatsapp: string | null };
      for (const i of (itens || []) as Linha[]) {
        const digitos = String(i.gestor_telefone || i.gestor_whatsapp || '').replace(/\D/g, '');
        const suf = digitos.length >= 8 ? digitos.slice(-8) : '';
        const atual = porPedido.get(i.pedido_id);
        // O pedido inteiro anda junto: vale a entrada mais ANTIGA entre os itens.
        if (!atual || (i.entrada_coluna_em && i.entrada_coluna_em < atual.desde)) {
          porPedido.set(i.pedido_id, { desde: i.entrada_coluna_em || atual?.desde || '', suf: suf || atual?.suf || '' });
        }
        if (i.entrada_coluna_em && (!maisAntigo || i.entrada_coluna_em < maisAntigo)) {
          maisAntigo = i.entrada_coluna_em;
        }
      }

      const horasParado = maisAntigo
        ? Math.floor((Date.now() - new Date(maisAntigo).getTime()) / 3_600_000)
        : 0;

      // Quantos já foram ligados / receberam WhatsApp DEPOIS de cair na coluna.
      // A correlação roda no banco (uma ida só) — cruzar gravações e mensagens
      // no navegador seria uma consulta por pedido.
      // A MESMA função que a triagem usa, devolvendo o detalhe por pedido — o
      // chip só soma. Uma regra só: se o contador da linha e o do tooltip viessem
      // de funções diferentes, divergiriam no primeiro ajuste.
      let comLigacao = 0, comWhatsapp = 0, semContato = 0;
      const alvos = Array.from(porPedido.values()).filter((p) => p.suf && p.desde);
      if (alvos.length > 0) {
        const { data: detalhe } = await supabase.rpc('crm_biometria_contatos', {
          p_pedidos: alvos.map((p) => ({ suf: p.suf, desde: p.desde })),
        });
        const porSufixo = new Map<string, { ligacoes: number; whats: number }>();
        for (const l of (detalhe || []) as { suf: string; ligacoes: number; whats: number }[]) {
          porSufixo.set(l.suf, { ligacoes: l.ligacoes ?? 0, whats: l.whats ?? 0 });
        }
        for (const p of alvos) {
          const c = porSufixo.get(p.suf);
          if ((c?.ligacoes ?? 0) > 0) comLigacao++;
          if ((c?.whats ?? 0) > 0) comWhatsapp++;
          if (!c || (c.ligacoes === 0 && c.whats === 0)) semContato++;
        }
      }

      return {
        pedidos: porPedido.size,
        itens: (itens || []).length,
        horasMaisAntigo: horasParado,
        comLigacao, comWhatsapp, semContato,
      };
    },
  });

  return {
    pedidos: data?.pedidos ?? 0,
    itens: data?.itens ?? 0,
    horasMaisAntigo: data?.horasMaisAntigo ?? 0,
    comLigacao: data?.comLigacao ?? 0,
    comWhatsapp: data?.comWhatsapp ?? 0,
    semContato: data?.semContato ?? 0,
  };
}
